import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  DataType,
  IAddIPDWizard,
  ICompareDocsItems,
  IIPDAnsweredQuestion,
  IIPDBill,
  IIPDBillListFetchResp,
  IIPDBillMapping,
  IIPDBillMappingFetchResp,
  IIPDBillsState,
  IIPDDoc,
  IIPDDocS3Data,
  IIPDErrorResp,
  IIPDFetchQuestionAnswersResp,
  IIPDHealthCareBillHeader,
  IIPDHealthCareBillHeaderFetchResp,
  IIPDListFilters,
  IIPDManualQuestion,
  IIPDPatientPayableFetchResp,
  IIPDSchemeBillHeader,
  IIPDSchemeBillImplantItem,
  IIPDSchemeBillMedicalItem,
  IIPDSchemeBillPackageItem,
  IIPDSchemeBillPatientPayableItem,
  IIPDSchemeHeaderFetchResp,
  IIPDSchemeHeaderUpdate,
  IIPDSchemeIPDBillItem,
  ISchemeMappingsInHealthCare,
} from "../interfaces/IIPDBills";
import {
  fetchIPDBills,
  fetchIPDQuestionnaires,
  getEditIPDBillMapping,
  getHealthCarePatientPayable,
  getIPDBillSchemeHeader,
  getIPDQuestionAnswerCollection,
  getNewIPDBillHealthCareBillHeader,
  getNewIPDBillMapping,
  postQuestionAnswersCollection,
  updateSchemeBillItems,
  uploadIPDDocToServer,
  callApproveIPDBill,
  downloadAbstractExcel,
  downloadAbstractZip,
  resetIPDSchemeBillItem,
} from "../apis/ipdBillingService";
import { ADD_IPD_BILL_WIZARD_ITEMS } from "../utils/constants";
import { errorMessage, successMessage } from "../utils/notificationUtils";
import {
  fetchNewIPDHealthCareBillHeaderUtil,
  resetIPDBillStateUtil,
  updateIPDBillMappingsUtil,
  updateSchemeDataUtil,
  updateSchemePackageUtil,
} from "../utils/ipdBillingUtils";
import { IOPDDoc } from "../interfaces/IOPDBills";

const initialState: IIPDBillsState = {
  isFetchingBillList: false,
  pageToIPDBillList: {},
  addIPDBill: {
    localFiles: {},
    localFiles2: [],
    allWizardSteps: ADD_IPD_BILL_WIZARD_ITEMS,
    ipdManualQuestions: {
      ipdQuestionnaires: {},
      isFetchingQuestionnaires: false,
      currentQuestionId: 1,
      answeredQuestionsResponse: {
        cardCategory: "",
        conclusion: "",
        healthCareBillItemsResponses: {
          healthCareBillIpdItems: [],
          healthCareBillMedicalItems: [],
        },
        packageDates: [],
        questionAnswersCollectedResponses: [],
        tataMemorialRateApply: "",
      },
      isConclusionGenerated: false,
    },
    billMappings: null,
    finalScreen: {
      healthCareBillIdDataMap: {},
      schemeBillHeaderIdDataMap: {},
      billMappingMap: {},
      collections: [],
      allHealthCareBillItems: {
        ipd: {},
        medical: {},
      },
      allSchemeBillItems: {
        cghs: {
          implant: {},
          ipd: {},
          package: {},
          medical: {},
        },
        patientPayable: {
          ipd: {},
          medical: {},
        },
      },
    },
    errors: {
      errorBillMapping: null,
      errorHealthCareBillHeader: null,
      errorHealthCarePatientPayable: null,
      errorQuestionAnswerCollection: null,
      errorBillSchemeHeader: null,
      errorPostQuestionAnswerCollection: null,
      errorInUpdatingSchemeBillItems: null,
      errorInApprovingIPDBill: null,
    },
    currState: {
      currBillMappingId: null,
      currSchemeBillHeaderId: null,
      currHealthCareBillHeaderId: null,
      wizardCurrScreen: 0,
      claimId: null,
      arePackagedItemsHighlighted: false,
      arePatientPayableItemsHighlighted: false,
    },
    loaders: {
      isSchemeBillItemsUpdating: false,
      isFetchIPDBillData: false,
      isResetBillItems: false,
      isApprovingIPDBill: false,
    },
    relatedDocs: {},
    filters: {
      searchFiltersByComponents: {
        scheme: {
          cghs: {
            ipd: "",
            implant: "",
            package: "",
            medical: "",
          },
          patientPayable: {
            ipd: "",
            medical: "",
          },
        },
        healthCare: {
          ipd: "",
          medical: "",
        },
      },
    },
  },
  ipdListFilters: {
    searchTerm: "",
    orderBy: "DESC",
    pageNumber: 1,
    pageSize: 10,
    startDate: null,
    endDate: null,
  },
  ipdBillListCurrState: {
    totalElements: 0,
  },
  necessaryDetailsForIPD: {},
};

export const getIPDBillList = createAsyncThunk(
  "ipdBills/getIPDBillsList",
  async ({
    hospitalId,
    filters,
  }: {
    hospitalId: number;
    filters: IIPDListFilters;
  }) => {
    const response = await fetchIPDBills(hospitalId, filters);
    return response;
  }
);

export const approveIPDBill = createAsyncThunk(
  "ipdBills/approveIPDBill",
  async ({
    hospitalId,
    currBillMappingId,
    currHealthCareBillHeaderId,
    currSchemeBillHeaderId,
    status,
  }: {
    hospitalId: number | undefined;
    currBillMappingId: number | undefined;
    currHealthCareBillHeaderId: number | undefined;
    currSchemeBillHeaderId: number | undefined;
    status: string;
  }) => {
    const response = await callApproveIPDBill(
      hospitalId,
      currBillMappingId,
      currHealthCareBillHeaderId,
      currSchemeBillHeaderId,
      status
    );
    return response;
  }
);

export const getIPDQuestionnaire = createAsyncThunk(
  "ipdBills/getIPDQuestionnaire",
  async ({ accessToken }: { accessToken: string }) => {
    const response = await fetchIPDQuestionnaires(accessToken);
    return response;
  }
);

export const generateNewIPDBillMapping = createAsyncThunk(
  "ipdBills/generateNewIPDBillMapping",
  async ({
    accessToken,
    hospitalId,
    documentPaths,
    billMapping,
  }: {
    accessToken: string;
    hospitalId: number;
    documentPaths: Array<IIPDDocS3Data>;
    billMapping: IIPDBillMapping | {};
  }) => {
    const response = await getNewIPDBillMapping(
      accessToken,
      hospitalId,
      documentPaths,
      billMapping
    );
    return response;
  }
);

export const fetchEditIPDBillMapping = createAsyncThunk(
  "ipdBills/fetchEditIPDBillMapping",
  async ({
    accessToken,
    hospitalId,
    ipdBillId,
  }: {
    accessToken: string;
    hospitalId: number;
    ipdBillId: string;
  }) => {
    const response = await getEditIPDBillMapping(
      accessToken,
      hospitalId,
      ipdBillId
    );
    return response;
  }
);

export const fetchNewIPDHealthCareBillHeader = createAsyncThunk(
  "ipdBills/fetchNewIPDHealthCareBillHeader",
  async ({
    accessToken,
    healthCareBillHeaderId,
  }: {
    accessToken: string;
    healthCareBillHeaderId: number;
  }) => {
    const response = await getNewIPDBillHealthCareBillHeader(
      accessToken,
      healthCareBillHeaderId
    );
    return response;
  }
);

export const fetchHealthCarePatientPayable = createAsyncThunk(
  "ipdBills/fetchHealthCarePatientPayable ",
  async ({
    accessToken,
    healthCareBillHeaderId,
  }: {
    accessToken: string;
    healthCareBillHeaderId: number;
  }) => {
    const response = await getHealthCarePatientPayable(
      accessToken,
      healthCareBillHeaderId
    );
    return response;
  }
);

export const fetchIPDQuestionAnswerCollection = createAsyncThunk(
  "ipdBills/fetchIPDQuestionAnswerCollection ",
  async ({
    accessToken,
    billMappingId,
  }: {
    accessToken: string;
    billMappingId: number;
  }) => {
    const response = await getIPDQuestionAnswerCollection(
      accessToken,
      billMappingId
    );
    return response;
  }
);

export const fetchIPDBillSchemeHeader = createAsyncThunk(
  "ipdBills/fetchIPDBillSchemeHeader ",
  async ({
    accessToken,
    schemeBillHeaderId,
  }: {
    accessToken: string;
    schemeBillHeaderId: number;
  }) => {
    const response = await getIPDBillSchemeHeader(
      accessToken,
      schemeBillHeaderId
    );
    return response;
  }
);

export const getAbstractExcel = createAsyncThunk(
  "ipdBills/getAbstractExcel ",
  async ({ currSchemeBillHeaderId }: { currSchemeBillHeaderId: number }) => {
    const response = await downloadAbstractExcel(currSchemeBillHeaderId);
    return response;
  }
);

export const getAbstractZip = createAsyncThunk(
  "ipdBills/getAbstractZip ",
  async ({
    hospitalId,
    dateFrom,
    dateTo,
  }: {
    hospitalId: number | undefined;
    dateFrom: string | undefined;
    dateTo: string | undefined;
  }) => {
    const response = await downloadAbstractZip(hospitalId, dateFrom, dateTo);
    return response;
  }
);

export const submitQuestionAnswersCollection = createAsyncThunk(
  "ipdBills/submitQuestionAnswersCollection",
  async ({
    answeredQuestions,
  }: {
    answeredQuestions: Array<IIPDAnsweredQuestion>;
  }) => {
    const response = await postQuestionAnswersCollection(answeredQuestions);
    return response;
  }
);

export const resetSchemeBillItem = createAsyncThunk(
  "ipdBills/resetSchemeBillItem",
  async ({
    accessToken,
    schemeBillHeaderId,
  }: {
    accessToken: string;
    schemeBillHeaderId: number | null;
  }) => {
    const response = await resetIPDSchemeBillItem(
      accessToken,
      schemeBillHeaderId
    );
    return response;
  }
);

export const uploadIPDDoc = createAsyncThunk(
  "ipdBills/uploadIPDDoc",
  async ({
    file,
    accessToken,
    docType,
    patientId,
    caseId,
    hospitalId,
    visitId
  }: {
    file: IIPDDoc;
    accessToken: string;
    hospitalId: string | null;
    docType: string;
    patientId: string | null;
    caseId: string | null;
    visitId: string | null
  }) => {
    const response = await uploadIPDDocToServer(
      file.originFileObj,
      accessToken,
      docType,
      patientId,
      caseId,
      hospitalId,
      visitId
    );
    return response;
  }
);

export const uploadRelatedDoc = createAsyncThunk(
  "ipdBills/uploadRelatedDoc",
  async ({
    file,
    docType,
    accessToken,
    hospitalId,
    visitId
  }: {
    file: object;
    docType: string;
    accessToken: string;
    hospitalId: string;
    visitId: string | null
  }) => {
    const response = await uploadIPDDocToServer(
      file,
      accessToken,
      docType,
      "0",
      "0",
      hospitalId,
      visitId
    );
    return response;
  }
);
export const updateSchemeBills = createAsyncThunk(
  "ipdBills/updateSchemeBills",
  async (
    {
      hospitalId,
      billMappingId,
      schemeBillHeaderId,
      schemeBillData,
      relatedDocs,
      accessToken,
      visitId
    }: {
      hospitalId: number | undefined;
      billMappingId: number | null;
      schemeBillHeaderId: number | null;
      schemeBillData: IIPDSchemeHeaderUpdate;
      relatedDocs: { [key: string]: ICompareDocsItems };
      accessToken: string;
      visitId: string | null;
    },
    { dispatch, getState }
  ) => {
    const uploadPromises = Object.keys(relatedDocs)?.map((key) => {
      if (relatedDocs[key].isUploaded === false && relatedDocs[key].fileObj) {
        return dispatch(
          uploadRelatedDoc({
            file: relatedDocs[key].fileObj,
            accessToken: accessToken,
            hospitalId: hospitalId?.toString() || "",
            docType: key,
            visitId: visitId
          })
        );
      }
      return Promise.resolve();
    });

    await Promise.all(uploadPromises);
    const updatedState: any = getState();
    const currentHeaderId =
      updatedState.ipdBills.addIPDBill.currState.currSchemeBillHeaderId;
    // Using updatedState here to get latest data of related docs
    schemeBillData.schemeBillHeader =
      updatedState.ipdBills.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
        currentHeaderId
      ].billHeader;

    const response = await updateSchemeBillItems(
      hospitalId,
      billMappingId,
      schemeBillHeaderId,
      schemeBillData
    );
    if (response) {
      dispatch(resetSchemeBills());
    }
    return response;
  }
);

const ipdBillsSlice = createSlice({
  name: "ipdBills",
  initialState,
  reducers: {
    clearNecessaryDetailsForIPD: (state, action: PayloadAction<string>) => {
      state.necessaryDetailsForIPD = {};
    },
    updateAddIPDBillWizardCurrScreenNumber: (
      state,
      action: PayloadAction<number>
    ) => {
      state.addIPDBill.currState.wizardCurrScreen = action.payload;
    },
    updateCardCategory: (state, action: PayloadAction<string>) => {
      if (state.addIPDBill.currState.currSchemeBillHeaderId) {
        state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          state.addIPDBill.currState.currSchemeBillHeaderId
        ].billHeader = {
          ...state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addIPDBill.currState.currSchemeBillHeaderId
          ].billHeader,
          cardCategory: action.payload,
        };
      }

      successMessage(`Successfully update card category to: ${action.payload}`);
    },
    updateObservations: (state, action: PayloadAction<string>) => {
      if (state.addIPDBill.currState.currSchemeBillHeaderId) {
        state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          state.addIPDBill.currState.currSchemeBillHeaderId
        ].billHeader = {
          ...state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addIPDBill.currState.currSchemeBillHeaderId
          ].billHeader,
          observation: action.payload,
        };
      }

      successMessage(`Successfully updated observations`);
    },
    addAnsweredQuestion: (
      state,
      action: PayloadAction<{
        newAnswer: IIPDAnsweredQuestion;
        newQusId: number;
      }>
    ) => {
      const { newAnswer, newQusId } = action.payload;

      state.addIPDBill.ipdManualQuestions.answeredQuestionsResponse.questionAnswersCollectedResponses.push(
        newAnswer
      );
      if (newQusId)
        state.addIPDBill.ipdManualQuestions.currentQuestionId = newQusId;
    },
    editAnsweredQuestion: (
      state,
      action: PayloadAction<{
        ansIdx: number;
        newQusId: number;
      }>
    ) => {
      const { ansIdx, newQusId } = action.payload;
      // state.addIPDBill.ipdManualQuestions.answeredQuestionsResponse.questionAnswersCollectedResponses.splice(
      //   ansIdx
      // );
      // state.addIPDBill.ipdManualQuestions.currentQuestionId = newQusId;
      // Find the index of the current questionId
      const currentQuestionIndex =
        state.addIPDBill.ipdManualQuestions.answeredQuestionsResponse.questionAnswersCollectedResponses.findIndex(
          (question) => question.questionId === newQusId
        );

      // Remove the current question and all questions after it
      if (currentQuestionIndex !== -1) {
        state.addIPDBill.ipdManualQuestions.answeredQuestionsResponse.questionAnswersCollectedResponses.splice(
          currentQuestionIndex // Start removing from the current index
        );
      }

      state.addIPDBill.ipdManualQuestions.currentQuestionId = newQusId;
      state.addIPDBill.ipdManualQuestions.isConclusionGenerated = false;
    },
    resetAnsweredQuestions: (state) => {
      state.addIPDBill.ipdManualQuestions.answeredQuestionsResponse.questionAnswersCollectedResponses =
        [];
      state.addIPDBill.ipdManualQuestions.currentQuestionId =
        state.addIPDBill.ipdManualQuestions.ipdQuestionnaires[1].id;
      state.addIPDBill.ipdManualQuestions.isConclusionGenerated = false;
    },
    resetCurrBillState: (state) => {
      state.addIPDBill.currState = {
        currBillMappingId: null,
        currSchemeBillHeaderId: null,
        currHealthCareBillHeaderId: null,
        wizardCurrScreen: 0,
        claimId: null,
        arePackagedItemsHighlighted: false,
        arePatientPayableItemsHighlighted: false,
      };
    },
    resetIPDManualQues: (state) => {
      state.addIPDBill.ipdManualQuestions = {
        ipdQuestionnaires: {},
        isFetchingQuestionnaires: false,
        currentQuestionId: 1,
        answeredQuestionsResponse: {
          cardCategory: "",
          conclusion: "",
          healthCareBillItemsResponses: {
            healthCareBillIpdItems: [],
            healthCareBillMedicalItems: [],
          },
          packageDates: [],
          questionAnswersCollectedResponses: [],
          tataMemorialRateApply: "",
        },
        isConclusionGenerated: false,
      };
    },
    updateAddIPDBillWizardCurrScreenValues: (
      state,
      action: PayloadAction<{
        currScreen: number;
        newWizardValues: IAddIPDWizard;
      }>
    ) => {
      const { currScreen, newWizardValues } = action.payload;
      state.addIPDBill.allWizardSteps[currScreen] = newWizardValues;
    },
    resetAddIPDBillState: (state) => {
      resetIPDBillStateUtil(state);
    },
    addIPDBillUploadedFile: (
      state,
      action: PayloadAction<{ file: IIPDDoc; docType: string }>
    ) => {
      const file = action.payload.file;
      const key = action.payload.docType;
      state.addIPDBill.localFiles[`${key}`] = file;
    },
    setAnsweredQuestionResponse: (state, action) => {
      state.addIPDBill.ipdManualQuestions.answeredQuestionsResponse =
        action.payload;
      if (action.payload?.conclusion !== "") {
        // state.addIPDBill.ipdManualQuestions.isConclusionGenerated = true;
        const ipdQuestionnairesArray = Object.values(
          state.addIPDBill.ipdManualQuestions.ipdQuestionnaires
        );
        const lastAnswered =
          state.addIPDBill.ipdManualQuestions.answeredQuestionsResponse
            .questionAnswersCollectedResponses[
            state.addIPDBill.ipdManualQuestions.answeredQuestionsResponse
              .questionAnswersCollectedResponses.length - 1
          ];
        const question = ipdQuestionnairesArray.find(
          (data) => data.id === lastAnswered.questionId
        );
        const answerList = question?.answersList;
        const newQuestId = answerList?.find(
          (data) => data.answer == lastAnswered.answerTxt
        );
        if (newQuestId)
          state.addIPDBill.ipdManualQuestions.currentQuestionId =
            newQuestId.nextQuestionIdFromAnswer;
      }
    },
    updateSchemeBillItem: (
      state,
      action: PayloadAction<{
        category: "cghs" | "patientPayable";
        subCategory: "ipd" | "implant" | "medical" | "package";
        item:
          | IIPDSchemeBillImplantItem
          | IIPDSchemeIPDBillItem
          | IIPDSchemeBillPackageItem
          | IIPDSchemeBillMedicalItem
          | IIPDSchemeBillPatientPayableItem;
      }>
    ) => {
      const { category, subCategory, item } = action.payload;
      if (category === "cghs") {
        state.addIPDBill.finalScreen.allSchemeBillItems[category][subCategory][
          item.id
        ] = item;
      } else if (
        category === "patientPayable" &&
        (subCategory === "ipd" || subCategory === "medical")
      ) {
        state.addIPDBill.finalScreen.allSchemeBillItems.patientPayable[
          subCategory
        ][item.id] = item;
      }
    },
    removeIPDBillUploadedFile: (
      state,
      action: PayloadAction<{ docType: string }>
    ) => {
      const key = action.payload.docType;
      const { [key]: removed, ...rest } = state.addIPDBill.localFiles;
      state.addIPDBill.localFiles = rest;
    },

    removeIPDBillUploadedFile2: (
      state,
      action: PayloadAction<{ docType: IOPDDoc }>
    ) => {
      const key = action?.payload?.docType;

      if (key?.id) {
        state.addIPDBill.localFiles2 = state.addIPDBill.localFiles2?.filter(
          (item: any) => item.id !== key?.id
        );
      }
    },
    removeSchemeBillItem: (
      state,
      action: PayloadAction<{
        item:
          | IIPDSchemeBillImplantItem
          | IIPDSchemeBillPackageItem
          | IIPDSchemeBillMedicalItem
          | IIPDSchemeIPDBillItem
          | IIPDSchemeBillPatientPayableItem
          | any;
        type:
          | "cghsIPD"
          | "cghsImplant"
          | "cghsMedical"
          | "cghsPackage"
          | "patientPayableIPD"
          | "patientPayableMedical";
      }>
    ) => {
      const { item, type } = action.payload;

      try {
        if (!state.addIPDBill.currState.currSchemeBillHeaderId) {
          throw new Error(
            "currSchemeBillHeaderId is not valid: " +
              state.addIPDBill.currState.currSchemeBillHeaderId
          );
        }

        //First loop over lids and update schemeMappings in allHealthCareBillItems
        item.lid?.map((lid: number) => {
          //Check if lid exists in healthcare ipd
          if (
            state.addIPDBill.finalScreen.allHealthCareBillItems.ipd.hasOwnProperty(
              lid
            )
          ) {
            state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
              lid
            ].schemeMappings =
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
                lid
              ].schemeMappings?.filter(
                (schemeMapping) =>
                  schemeMapping.itemMappingInSchemeId !== item.id
              );
          } else if (
            state.addIPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
              lid
            )
          ) {
            state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
              lid
            ].schemeMappings =
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                lid
              ].schemeMappings?.filter(
                (schemeMapping) =>
                  schemeMapping.itemMappingInSchemeId !== item.id
              );
          }
        });

        // Then remove the item from allHealthCareBillItems and schemeBillHeaderIdDataMap
        switch (type) {
          // case "cghsIPD":
          //   delete state.addIPDBill.finalScreen.allSchemeBillItems.cghs.ipd[
          //     item.id
          //   ];
          //   state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //     state.addIPDBill.currState.currSchemeBillHeaderId
          //   ].billItems.cghs.ipd[item.serviceCategory] =
          //     state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //       state.addIPDBill.currState.currSchemeBillHeaderId
          //     ].billItems.cghs.ipd[item.serviceCategory].filter(
          //       (id) => id !== item.id
          //     );

          //   // If the length of the key in schemeBillHeaderIdDataMap is 0, remove that key
          //   if (
          //     state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //       state.addIPDBill.currState.currSchemeBillHeaderId
          //     ].billItems.cghs.ipd[item.serviceCategory].length === 0
          //   ) {
          //     delete state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //       state.addIPDBill.currState.currSchemeBillHeaderId
          //     ].billItems.cghs.ipd[item.serviceCategory];
          //   }
          //   successMessage("the item has been removed from the scheme bill", 3);
          //   return;
          case "cghsIPD":
            delete state.addIPDBill.finalScreen.allSchemeBillItems.cghs.ipd[
              item.id
            ];
            state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.cghs.ipd[item.collection] =
              state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addIPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.ipd[item.collection]?.filter(
                (id) => id !== item.id
              );

            // If the length of the key in schemeBillHeaderIdDataMap is 0, remove that key
            if (
              state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addIPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.ipd[item.collection].length === 0
            ) {
              delete state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addIPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.ipd[item.collection];
            }
            successMessage("the item has been removed from the scheme bill", 3);
            return;
          // case "cghsImplant":
          //   delete state.addIPDBill.finalScreen.allSchemeBillItems.cghs.implant[
          //     item.id
          //   ];
          //   state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //     state.addIPDBill.currState.currSchemeBillHeaderId
          //   ].billItems.cghs.implant[item.serviceCategory] =
          //     state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //       state.addIPDBill.currState.currSchemeBillHeaderId
          //     ].billItems.cghs.implant[item.serviceCategory].filter(
          //       (id) => id !== item.id
          //     );
          //   // If the length of the key in schemeBillHeaderIdDataMap is 0, remove that key
          //   if (
          //     state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //       state.addIPDBill.currState.currSchemeBillHeaderId
          //     ].billItems.cghs.implant[item.serviceCategory].length === 0
          //   ) {
          //     delete state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //       state.addIPDBill.currState.currSchemeBillHeaderId
          //     ].billItems.cghs.implant[item.serviceCategory];
          //   }
          //   return;
          case "cghsImplant":
            delete state.addIPDBill.finalScreen.allSchemeBillItems.cghs.implant[
              item.id
            ];
            state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.cghs.implant[item.collection] =
              state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addIPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.implant[item.collection]?.filter(
                (id) => id !== item.id
              );
            // If the length of the key in schemeBillHeaderIdDataMap is 0, remove that key
            if (
              state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addIPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.implant[item.collection].length === 0
            ) {
              delete state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addIPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.implant[item.collection];
            }
            return;
          // case "cghsMedical":
          //   delete state.addIPDBill.finalScreen.allSchemeBillItems.cghs.medical[
          //     item.id
          //   ];
          //   state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //     state.addIPDBill.currState.currSchemeBillHeaderId
          //   ].billItems.cghs.medical[item.serviceCategory] =
          //     state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //       state.addIPDBill.currState.currSchemeBillHeaderId
          //     ].billItems.cghs.medical[item.serviceCategory].filter(
          //       (id) => id !== item.id
          //     );

          //   // If the length of the key in schemeBillHeaderIdDataMap is 0, remove that key
          //   if (
          //     state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //       state.addIPDBill.currState.currSchemeBillHeaderId
          //     ].billItems.cghs.medical[item.serviceCategory].length === 0
          //   ) {
          //     delete state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //       state.addIPDBill.currState.currSchemeBillHeaderId
          //     ].billItems.cghs.medical[item.serviceCategory];
          //   }
          //   return;
          case "cghsMedical":
            delete state.addIPDBill.finalScreen.allSchemeBillItems.cghs.medical[
              item.id
            ];
            state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.cghs.medical[item.collection] =
              state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addIPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.medical[item.collection]?.filter(
                (id) => id !== item.id
              );

            // If the length of the key in schemeBillHeaderIdDataMap is 0, remove that key
            if (
              state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addIPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.medical[item.collection].length === 0
            ) {
              delete state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addIPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.medical[item.collection];
            }
            return;
          // case "cghsPackage":
          //   delete state.addIPDBill.finalScreen.allSchemeBillItems.cghs.package[
          //     item.id
          //   ];
          //   state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //     state.addIPDBill.currState.currSchemeBillHeaderId
          //   ].billItems.cghs.package[item.serviceCategory] =
          //     state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //       state.addIPDBill.currState.currSchemeBillHeaderId
          //     ].billItems.cghs.package[item.serviceCategory].filter(
          //       (id) => id !== item.id
          //     );

          //   // If the length of the key in schemeBillHeaderIdDataMap is 0, remove that key
          //   if (
          //     state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //       state.addIPDBill.currState.currSchemeBillHeaderId
          //     ].billItems.cghs.package[item.serviceCategory].length === 0
          //   ) {
          //     delete state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //       state.addIPDBill.currState.currSchemeBillHeaderId
          //     ].billItems.cghs.package[item.serviceCategory];
          //   }
          //   return;
          case "cghsPackage":
            delete state.addIPDBill.finalScreen.allSchemeBillItems.cghs.package[
              item.id
            ];
            state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.cghs.package["PACKAGE"] =
              state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addIPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.package["PACKAGE"]?.filter(
                (id) => id !== item.id
              );

            // If the length of the key in schemeBillHeaderIdDataMap is 0, remove that key
            if (
              state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addIPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.package["PACKAGE"].length === 0
            ) {
              delete state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addIPDBill.currState.currSchemeBillHeaderId
              ].billItems.cghs.package["PACKAGE"];
            }
            return;
          // case "patientPayableIPD":
          //   delete state.addIPDBill.finalScreen.allSchemeBillItems
          //     .patientPayable.ipd[item.id];
          //   state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //     state.addIPDBill.currState.currSchemeBillHeaderId
          //   ].billItems.patientPayable.ipd[item.serviceCategory] =
          //     state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //       state.addIPDBill.currState.currSchemeBillHeaderId
          //     ].billItems.patientPayable.ipd[item.serviceCategory].filter(
          //       (id) => id !== item.id
          //     );

          //   // If the length of the key in schemeBillHeaderIdDataMap is 0, remove that key
          //   if (
          //     state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //       state.addIPDBill.currState.currSchemeBillHeaderId
          //     ].billItems.patientPayable.ipd[item.serviceCategory].length === 0
          //   ) {
          //     delete state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //       state.addIPDBill.currState.currSchemeBillHeaderId
          //     ].billItems.patientPayable.ipd[item.serviceCategory];
          //   }
          //   return;
          case "patientPayableIPD":
            delete state.addIPDBill.finalScreen.allSchemeBillItems
              .patientPayable.ipd[item.id];
            state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.patientPayable.ipd[item.collection] =
              state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addIPDBill.currState.currSchemeBillHeaderId
              ].billItems.patientPayable.ipd[item.collection]?.filter(
                (id) => id !== item.id
              );

            // If the length of the key in schemeBillHeaderIdDataMap is 0, remove that key
            if (
              state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addIPDBill.currState.currSchemeBillHeaderId
              ].billItems.patientPayable.ipd[item.collection].length === 0
            ) {
              delete state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addIPDBill.currState.currSchemeBillHeaderId
              ].billItems.patientPayable.ipd[item.collection];
            }
            return;
          // case "patientPayableMedical":
          //   delete state.addIPDBill.finalScreen.allSchemeBillItems
          //     .patientPayable.medical[item.id];
          //   state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //     state.addIPDBill.currState.currSchemeBillHeaderId
          //   ].billItems.patientPayable.medical[item.serviceCategory] =
          //     state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //       state.addIPDBill.currState.currSchemeBillHeaderId
          //     ].billItems.patientPayable.medical[item.serviceCategory].filter(
          //       (id) => id !== item.id
          //     );

          //   // If the length of the key in schemeBillHeaderIdDataMap is 0, remove that key
          //   if (
          //     state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //       state.addIPDBill.currState.currSchemeBillHeaderId
          //     ].billItems.patientPayable.medical[item.serviceCategory]
          //       .length === 0
          //   ) {
          //     delete state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          //       state.addIPDBill.currState.currSchemeBillHeaderId
          //     ].billItems.patientPayable.medical[item.serviceCategory];
          //   }
          //   return;
          case "patientPayableMedical":
            delete state.addIPDBill.finalScreen.allSchemeBillItems
              .patientPayable.medical[item.id];
            state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.patientPayable.medical[item.collection] =
              state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addIPDBill.currState.currSchemeBillHeaderId
              ].billItems.patientPayable.medical[item.collection]?.filter(
                (id) => id !== item.id
              );

            // If the length of the key in schemeBillHeaderIdDataMap is 0, remove that key
            if (
              state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addIPDBill.currState.currSchemeBillHeaderId
              ].billItems.patientPayable.medical[item.collection].length === 0
            ) {
              delete state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                state.addIPDBill.currState.currSchemeBillHeaderId
              ].billItems.patientPayable.medical[item.collection];
            }
            return;
          default:
            throw new Error("Type not supported: " + type);
        }
      } catch (error) {
        errorMessage("Error in removing item");
      }
    },
    updateSchemePackage: (
      state,
      action: PayloadAction<{ packages: Array<IIPDSchemeBillPackageItem> }>
    ) => {
      updateSchemePackageUtil(state, action);
    },

    toggleHighlightPackagedItems: (state) => {
      state.addIPDBill.currState.arePackagedItemsHighlighted =
        !state.addIPDBill.currState.arePackagedItemsHighlighted;
    },
    toggleHighlightPatientPayableItems: (state) => {
      state.addIPDBill.currState.arePatientPayableItemsHighlighted =
        !state.addIPDBill.currState.arePatientPayableItemsHighlighted;
    },
    updateIPDHealthCareBillItem: (
      state,
      action: PayloadAction<{ row: any; eventDatType: string }>
    ) => {
      const { row: updatedRow, eventDatType } = action.payload;

      const {
        key,
        qty,
        rate,
        total,
        name,
        date,
        collection,
        category,
        ...rowWithoutKey
      } = updatedRow;

      const billType = eventDatType === "ipd" ? "ipd" : "medical";

      const existingItems =
        state.addIPDBill.finalScreen.allHealthCareBillItems[billType];
      const existingRow = existingItems ? existingItems[key] : undefined;
      if (!existingRow) {
        console.error(`No existing row found for key: ${key}`);
        return; // Exit if the row does not exist
      }

      const newRow = {
        ...rowWithoutKey,
        id: key,
        serviceAmount: total,
        serviceCategory: category,
        serviceDate: date,
        serviceName: name,
        serviceQty: qty,
        serviceRate: rate,
        collection: collection,
        schemeMappings: existingRow.schemeMappings, // Preserve schemeMappings from the existing row
      };

      // Update the state
      if (billType === "ipd") {
        existingItems[key] = newRow; // Mutate the existing object safely
      } else if (billType === "medical") {
        existingItems[key] = newRow; // Mutate the existing object safely
      }
    },
    updateMedicalHealthCareBillItem: (
      state,
      action: PayloadAction<{ row: any; eventDatType: string }>
    ) => {
      const { row: updatedRow, eventDatType } = action.payload;

      const {
        key,
        name,
        billDate,
        qty,
        rate,
        total,
        drugType,
        category,
        collection,
        genericName,
        batchNo,
        requestNo,
        requestDate,
        hsn,
        mrp,
        sellPrice,
        tax,

        ...rowWithoutKey
      } = updatedRow;

      const billType = eventDatType === "ipd" ? "ipd" : "medical";

      const existingItems =
        state.addIPDBill.finalScreen.allHealthCareBillItems[billType];
      const existingRow = existingItems ? existingItems[key] : undefined;
      if (!existingRow) {
        console.error(`No existing row found for key: ${key}`);
        return; // Exit if the row does not exist
      }

      const newRow = {
        ...rowWithoutKey,
        id: key,
        serviceName: name,
        billDate: billDate,
        qty: qty,
        rate: rate,
        total: total,
        drugType: drugType,
        serviceCategory: category,
        collection: collection,
        genericName: genericName,
        batchNumber: batchNo,
        requestNumber: requestNo,
        requestDate: requestDate,
        hsn: hsn,
        mrp: mrp,
        selPrice: sellPrice,
        tax: tax,
        schemeMappings: existingRow.schemeMappings, // Preserve schemeMappings from the existing row
      };

      // Update the state
      if (billType === "ipd") {
        existingItems[key] = newRow; // Mutate the existing object safely
      } else if (billType === "medical") {
        existingItems[key] = newRow; // Mutate the existing object safely
      }
    },
    saveRelatedDocLocally: (
      state,
      action: PayloadAction<{ key: string; doc: ICompareDocsItems }>
    ) => {
      const schemeBillHeaderId =
        state.addIPDBill.currState.currSchemeBillHeaderId;
      const { key, doc } = action.payload;
      if (schemeBillHeaderId && key && doc) {
        state.addIPDBill.relatedDocs[schemeBillHeaderId][key] = doc;
      }
    },
    updateClaimId: (state, action: PayloadAction<{ claimId: string }>) => {
      if (state.addIPDBill.currState.currSchemeBillHeaderId) {
        state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          state.addIPDBill.currState.currSchemeBillHeaderId
        ].billHeader = {
          ...state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addIPDBill.currState.currSchemeBillHeaderId
          ].billHeader,
          claimId: action.payload.claimId,
        };

        state.addIPDBill.currState.claimId = action.payload.claimId;

        successMessage("Claim Id updated!");
      } else {
        errorMessage("Cannot update claim id");
      }
    },
    updateLidsInPackage: (
      state,
      action: PayloadAction<{ newLids: Array<number>; packageId: number }>
    ) => {
      try {
        const { newLids, packageId } = action.payload;
        if (
          state.addIPDBill.finalScreen.allSchemeBillItems.cghs.package.hasOwnProperty(
            packageId
          )
        ) {
          const removedLids =
            state.addIPDBill.finalScreen.allSchemeBillItems.cghs.package[
              packageId
            ].lid?.filter((id) => !newLids.includes(id));

          // Loop over removed lids and update allHealthCareBillItems
          removedLids?.forEach((id) => {
            //Check if lid exists in healthcare ipd
            if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
                id
              ].schemeMappings =
                state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
                  id
                ].schemeMappings?.filter(
                  (schemeMapping) =>
                    schemeMapping.itemMappingInSchemeId !== packageId
                );
            } else if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                id
              ].schemeMappings =
                state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                  id
                ].schemeMappings?.filter(
                  (schemeMapping) =>
                    schemeMapping.itemMappingInSchemeId !== packageId
                );
            }
          });

          state.addIPDBill.finalScreen.allSchemeBillItems.cghs.package[
            packageId
          ].lid = newLids;
        }
      } catch (error) {
        console.error(error);
        errorMessage("Cannot remove items");
      }
    },
    // addCGHSIPDItemInScheme: (state, action: PayloadAction<{ item: any }>) => {

    //   try {
    //     if (!state.addIPDBill.currState.currSchemeBillHeaderId) {
    //       throw new Error("currSchemeBillHeaderId is not valid");
    //     }
    //     const { item } = action.payload;
    //     if (item) {
    //       state.addIPDBill.finalScreen.allSchemeBillItems.cghs.ipd[item?.id] =
    //         item;

    //       if (
    //         !state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
    //           state.addIPDBill.currState.currSchemeBillHeaderId
    //         ].billItems.cghs.ipd.hasOwnProperty(item.serviceCategory)
    //       ) {
    //         state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
    //           state.addIPDBill.currState.currSchemeBillHeaderId
    //         ].billItems.cghs.ipd[item.serviceCategory] = [item.id];
    //       } else {
    //         state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
    //           state.addIPDBill.currState.currSchemeBillHeaderId
    //         ].billItems.cghs.ipd[item.serviceCategory].push(item.id);
    //       }

    //       const schemeMapping: ISchemeMappingsInHealthCare = {
    //         itemMappingInSchemeCategory: "cghs",
    //         itemMappingInSchemeSubCategory: "ipd",
    //         itemMappingInSchemeServiceCategory: item.serviceCategory,
    //         itemMappingInSchemeId: item.id,
    //       };

    //       item.lid.forEach((lid: number) => {
    //         state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
    //           lid
    //         ].schemeMappings = [
    //           ...state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[lid]
    //             .schemeMappings,
    //           schemeMapping,
    //         ];
    //       });

    //       successMessage("Moved item to CGHS IPD in Scheme Bill", 4);
    //     }
    //   } catch (error) {
    //     console.error(error);
    //     errorMessage("Cannot add CGHS IPD item", 4);
    //   }
    // },
    addCGHSIPDItemInScheme: (state, action: PayloadAction<{ item: any }>) => {
      try {
        if (!state.addIPDBill.currState.currSchemeBillHeaderId) {
          throw new Error("currSchemeBillHeaderId is not valid");
        }
        const { item } = action.payload;
        if (item) {
          state.addIPDBill.finalScreen.allSchemeBillItems.cghs.ipd[item?.id] =
            item;

          if (
            !state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.cghs.ipd.hasOwnProperty(item.collection)
          ) {
            state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.cghs.ipd[item.collection] = [item.id];
          } else {
            state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.cghs.ipd[item.collection].push(item.id);
          }

          const schemeMapping: ISchemeMappingsInHealthCare = {
            itemMappingInSchemeCategory: "cghs",
            itemMappingInSchemeSubCategory: "ipd",
            itemMappingInSchemeServiceCategory: item.serviceCategory,
            itemMappingInSchemeId: item.id,
            itemMappingInSchemeCollection: item.collection,
          };

          item.lid?.forEach((lid: number) => {
            state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
              lid
            ].schemeMappings = [
              ...state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[lid]
                .schemeMappings,
              schemeMapping,
            ];
          });

          successMessage("Moved item to CGHS IPD in Scheme Bill", 4);
        }
      } catch (error) {
        console.error(error);
        errorMessage("Cannot add CGHS IPD item", 4);
      }
    },
    // addCGHSMedicalToIPDItemInScheme: (state, action: PayloadAction<{ item: any }>) => {

    //   try {
    //     if (!state.addIPDBill.currState.currSchemeBillHeaderId) {
    //       throw new Error("currSchemeBillHeaderId is not valid");
    //     }
    //     const { item } = action.payload;
    //     if (item) {
    //       state.addIPDBill.finalScreen.allSchemeBillItems.cghs.ipd[item?.id] =
    //         item;

    //       if (
    //         !state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
    //           state.addIPDBill.currState.currSchemeBillHeaderId
    //         ].billItems.cghs.ipd.hasOwnProperty(item.serviceCategory)
    //       ) {
    //         state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
    //           state.addIPDBill.currState.currSchemeBillHeaderId
    //         ].billItems.cghs.ipd[item.serviceCategory] = [item.id];
    //       } else {
    //         state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
    //           state.addIPDBill.currState.currSchemeBillHeaderId
    //         ].billItems.cghs.ipd[item.serviceCategory].push(item.id);
    //       }

    //       const schemeMapping: ISchemeMappingsInHealthCare = {
    //         itemMappingInSchemeCategory: "cghs",
    //         itemMappingInSchemeSubCategory: "ipd",
    //         itemMappingInSchemeServiceCategory: item.serviceCategory,
    //         itemMappingInSchemeId: item.id,
    //       };

    //       item.lid.forEach((lid: number) => {
    //         state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
    //           lid
    //         ].schemeMappings = [
    //           ...state.addIPDBill.finalScreen.allHealthCareBillItems.medical[lid]
    //             .schemeMappings,
    //           schemeMapping,
    //         ];
    //       });

    //       successMessage("Moved item to CGHS Medical to IPD in Scheme Bill", 4);
    //     }
    //   } catch (error) {
    //     console.error(error);
    //     errorMessage("Cannot add CGHS IPD item", 4);
    //   }
    // },
    addCGHSMedicalToIPDItemInScheme: (
      state,
      action: PayloadAction<{ item: any }>
    ) => {
      try {
        if (!state.addIPDBill.currState.currSchemeBillHeaderId) {
          throw new Error("currSchemeBillHeaderId is not valid");
        }
        const { item } = action.payload;
        if (item) {
          state.addIPDBill.finalScreen.allSchemeBillItems.cghs.ipd[item?.id] =
            item;

          if (
            !state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.cghs.ipd.hasOwnProperty(item.collection)
          ) {
            state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.cghs.ipd[item.collection] = [item.id];
          } else {
            state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.cghs.ipd[item.collection].push(item.id);
          }

          const schemeMapping: ISchemeMappingsInHealthCare = {
            itemMappingInSchemeCategory: "cghs",
            itemMappingInSchemeSubCategory: "ipd",
            itemMappingInSchemeServiceCategory: item.serviceCategory,
            itemMappingInSchemeId: item.id,
            itemMappingInSchemeCollection: item?.collection,
          };

          item.lid?.forEach((lid: number) => {
            state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
              lid
            ].schemeMappings = [
              ...state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                lid
              ].schemeMappings,
              schemeMapping,
            ];
          });

          successMessage("Moved item to CGHS Medical to IPD in Scheme Bill", 4);
        }
      } catch (error) {
        console.error(error);
        errorMessage("Cannot add CGHS IPD item", 4);
      }
    },
    // addCGHSMedicalItemInScheme: (
    //   state,
    //   action: PayloadAction<{ item: any }>
    // ) => {
    //   if (state.addIPDBill.currState.currSchemeBillHeaderId) {
    //     const { item } = action.payload;
    //     state.addIPDBill.finalScreen.allSchemeBillItems.cghs.medical[item.id] =
    //       item;

    //     if (
    //       !state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
    //         state.addIPDBill.currState.currSchemeBillHeaderId
    //       ].billItems.cghs.medical.hasOwnProperty(item.serviceCategory)
    //     ) {
    //       state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
    //         state.addIPDBill.currState.currSchemeBillHeaderId
    //       ].billItems.cghs.medical[item.serviceCategory] = [item.id];
    //     } else {
    //       state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
    //         state.addIPDBill.currState.currSchemeBillHeaderId
    //       ].billItems.cghs.medical[item.serviceCategory].push(item.id);
    //     }

    //     const schemeMapping: ISchemeMappingsInHealthCare = {
    //       itemMappingInSchemeCategory: "cghs",
    //       itemMappingInSchemeSubCategory: "medical",
    //       itemMappingInSchemeServiceCategory: item.serviceCategory,
    //       itemMappingInSchemeId: item.id,
    //     };

    //     item.lid.forEach((lid: number) => {
    //       state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
    //         lid
    //       ].schemeMappings = [
    //         ...state.addIPDBill.finalScreen.allHealthCareBillItems.medical[lid]
    //           .schemeMappings,
    //         schemeMapping,
    //       ];
    //     });
    //     successMessage("Moved item to CGHS Medical in Scheme Bill", 4);
    //   } else {
    //     errorMessage("Cannot add CGHS medical item");
    //   }
    // },
    addCGHSMedicalItemInScheme: (
      state,
      action: PayloadAction<{ item: any }>
    ) => {
      if (state.addIPDBill.currState.currSchemeBillHeaderId) {
        const { item } = action.payload;
        state.addIPDBill.finalScreen.allSchemeBillItems.cghs.medical[item.id] =
          item;

        if (
          !state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addIPDBill.currState.currSchemeBillHeaderId
          ].billItems.cghs.medical.hasOwnProperty(item.collection)
        ) {
          state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addIPDBill.currState.currSchemeBillHeaderId
          ].billItems.cghs.medical[item.collection] = [item.id];
        } else {
          state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addIPDBill.currState.currSchemeBillHeaderId
          ].billItems.cghs.medical[item.collection].push(item.id);
        }

        const schemeMapping: ISchemeMappingsInHealthCare = {
          itemMappingInSchemeCategory: "cghs",
          itemMappingInSchemeSubCategory: "medical",
          itemMappingInSchemeServiceCategory: item.serviceCategory,
          itemMappingInSchemeId: item.id,
          itemMappingInSchemeCollection: item.collection,
        };

        item.lid?.forEach((lid: number) => {
          state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
            lid
          ].schemeMappings = [
            ...state.addIPDBill.finalScreen.allHealthCareBillItems.medical[lid]
              .schemeMappings,
            schemeMapping,
          ];
        });
        successMessage("Moved item to CGHS Medical in Scheme Bill", 4);
      } else {
        errorMessage("Cannot add CGHS medical item");
      }
    },
    // addPatientPayableIPDItemInScheme: (
    //   state,
    //   action: PayloadAction<{ item: IIPDSchemeBillPatientPayableItem }>
    // ) => {
    //   if (state.addIPDBill.currState.currSchemeBillHeaderId) {
    //     const { item } = action.payload;
    //     state.addIPDBill.finalScreen.allSchemeBillItems.patientPayable.ipd[
    //       item.id
    //     ] = item;

    //     if (
    //       !state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
    //         state.addIPDBill.currState.currSchemeBillHeaderId
    //       ].billItems.patientPayable.ipd.hasOwnProperty(item.serviceCategory)
    //     ) {
    //       state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
    //         state.addIPDBill.currState.currSchemeBillHeaderId
    //       ].billItems.patientPayable.ipd[item.serviceCategory] = [item.id];
    //     } else {
    //       state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
    //         state.addIPDBill.currState.currSchemeBillHeaderId
    //       ].billItems.patientPayable.ipd[item.serviceCategory].push(item.id);
    //     }

    //     const schemeMapping: ISchemeMappingsInHealthCare = {
    //       itemMappingInSchemeCategory: "patientPayable",
    //       itemMappingInSchemeSubCategory: "ipd",
    //       itemMappingInSchemeServiceCategory: item.serviceCategory,
    //       itemMappingInSchemeId: item.id,
    //     };

    //     item.lid.forEach((lid) => {
    //       state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
    //         lid
    //       ].schemeMappings = [
    //         ...state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[lid]
    //           .schemeMappings,
    //         schemeMapping,
    //       ];
    //     });
    //     successMessage("Moved item to Patient Payable IPD in Scheme Bill", 4);
    //   } else {
    //     errorMessage("Cannot add Patient Payable IPD item");
    //   }
    // },
    addPatientPayableIPDItemInScheme: (
      state,
      action: PayloadAction<{ item: IIPDSchemeBillPatientPayableItem }>
    ) => {
      if (state.addIPDBill.currState.currSchemeBillHeaderId) {
        const { item } = action.payload;
        state.addIPDBill.finalScreen.allSchemeBillItems.patientPayable.ipd[
          item.id
        ] = item;

        if (item.collection) {
          if (
            !state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.patientPayable.ipd.hasOwnProperty(item.collection)
          ) {
            state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.patientPayable.ipd[item.collection] = [item.id];
          } else {
            state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.patientPayable.ipd[item.collection].push(item.id);
          }

          const schemeMapping: ISchemeMappingsInHealthCare = {
            itemMappingInSchemeCategory: "patientPayable",
            itemMappingInSchemeSubCategory: "ipd",
            itemMappingInSchemeServiceCategory: item.serviceCategory,
            itemMappingInSchemeId: item.id,
            itemMappingInSchemeCollection: item.collection,
          };

          item.lid?.forEach((lid) => {
            state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
              lid
            ].schemeMappings = [
              ...state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[lid]
                .schemeMappings,
              schemeMapping,
            ];
          });
          successMessage("Moved item to Patient Payable IPD in Scheme Bill", 4);
        }
      } else {
        errorMessage("Cannot add Patient Payable IPD item");
      }
    },
    // addPatientPayableMedicalItemInScheme: (
    //   state,
    //   action: PayloadAction<{ item: IIPDSchemeBillPatientPayableItem }>
    // ) => {
    //   if (state.addIPDBill.currState.currSchemeBillHeaderId) {
    //     const { item } = action.payload;
    //     state.addIPDBill.finalScreen.allSchemeBillItems.patientPayable.medical[
    //       item.id
    //     ] = item;

    //     if (
    //       !state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
    //         state.addIPDBill.currState.currSchemeBillHeaderId
    //       ].billItems.patientPayable.medical.hasOwnProperty(
    //         item.serviceCategory
    //       )
    //     ) {
    //       state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
    //         state.addIPDBill.currState.currSchemeBillHeaderId
    //       ].billItems.patientPayable.medical[item.serviceCategory] = [item.id];
    //     } else {
    //       state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
    //         state.addIPDBill.currState.currSchemeBillHeaderId
    //       ].billItems.patientPayable.medical[item.serviceCategory].push(
    //         item.id
    //       );
    //     }

    //     const schemeMapping: ISchemeMappingsInHealthCare = {
    //       itemMappingInSchemeCategory: "patientPayable",
    //       itemMappingInSchemeSubCategory: "medical",
    //       itemMappingInSchemeServiceCategory: item.serviceCategory,
    //       itemMappingInSchemeId: item.id,
    //     };

    //     item.lid.forEach((lid) => {
    //       state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
    //         lid
    //       ].schemeMappings = [
    //         ...state.addIPDBill.finalScreen.allHealthCareBillItems.medical[lid]
    //           .schemeMappings,
    //         schemeMapping,
    //       ];
    //     });
    //     successMessage(
    //       "Moved item to Patient Payable Medical in Scheme Bill",
    //       4
    //     );
    //   } else {
    //     errorMessage("Cannot add Patient Payable IPD item");
    //   }
    // },
    addPatientPayableMedicalItemInScheme: (
      state,
      action: PayloadAction<{ item: IIPDSchemeBillPatientPayableItem }>
    ) => {
      if (state.addIPDBill.currState.currSchemeBillHeaderId) {
        const { item } = action.payload;
        state.addIPDBill.finalScreen.allSchemeBillItems.patientPayable.medical[
          item.id
        ] = item;

        if (item.collection) {
          if (
            !state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.patientPayable.medical.hasOwnProperty(item.collection)
          ) {
            state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.patientPayable.medical[item.collection] = [item.id];
          } else {
            state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.patientPayable.medical[item.collection].push(item.id);
          }

          const schemeMapping: ISchemeMappingsInHealthCare = {
            itemMappingInSchemeCategory: "patientPayable",
            itemMappingInSchemeSubCategory: "medical",
            itemMappingInSchemeServiceCategory: item.serviceCategory,
            itemMappingInSchemeId: item.id,
            itemMappingInSchemeCollection: item?.collection,
          };

          item.lid.forEach((lid) => {
            state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
              lid
            ].schemeMappings = [
              ...state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                lid
              ].schemeMappings,
              schemeMapping,
            ];
          });
          successMessage(
            "Moved item to Patient Payable Medical in Scheme Bill",
            4
          );
        }
      } else {
        errorMessage("Cannot add Patient Payable IPD item");
      }
    },
    // addCGHSImplantItemInScheme: (
    //   state,
    //   action: PayloadAction<{
    //     item: IIPDSchemeBillImplantItem;
    //     healthCareCategory: "ipd" | "medical";
    //   }>
    // ) => {
    //   if (state.addIPDBill.currState.currSchemeBillHeaderId) {
    //     const { item, healthCareCategory } = action.payload;
    //     state.addIPDBill.finalScreen.allSchemeBillItems.cghs.implant[item.id] =
    //       item;

    //     if (
    //       !state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
    //         state.addIPDBill.currState.currSchemeBillHeaderId
    //       ].billItems.cghs.implant.hasOwnProperty(item.serviceCategory)
    //     ) {
    //       state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
    //         state.addIPDBill.currState.currSchemeBillHeaderId
    //       ].billItems.cghs.implant[item.serviceCategory] = [item.id];
    //     } else {
    //       state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
    //         state.addIPDBill.currState.currSchemeBillHeaderId
    //       ].billItems.cghs.implant[item.serviceCategory].push(item.id);
    //     }

    //     const schemeMapping: ISchemeMappingsInHealthCare = {
    //       itemMappingInSchemeCategory: "cghs",
    //       itemMappingInSchemeSubCategory: "implant",
    //       itemMappingInSchemeServiceCategory: item.serviceCategory,
    //       itemMappingInSchemeId: item.id,
    //     };

    //     item.lid.forEach((lid) => {
    //       // Check if lid exists in idp or medical healthcare items

    //       if (
    //         state.addIPDBill.finalScreen.allHealthCareBillItems.ipd.hasOwnProperty(
    //           lid
    //         )
    //       ) {
    //         state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
    //           lid
    //         ].schemeMappings = [
    //           ...state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[lid]
    //             .schemeMappings,
    //           schemeMapping,
    //         ];
    //       } else if (
    //         state.addIPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
    //           lid
    //         )
    //       ) {
    //         state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
    //           lid
    //         ].schemeMappings = [
    //           ...state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
    //             lid
    //           ].schemeMappings,
    //           schemeMapping,
    //         ];
    //       }
    //     });
    //     successMessage("Moved item to CGHS Implant in Scheme Bill", 4);
    //   } else {
    //     errorMessage("Cannot add CGHS Implant item");
    //   }
    // },

    addCGHSImplantItemInScheme: (
      state,
      action: PayloadAction<{
        item: IIPDSchemeBillImplantItem;
        healthCareCategory: "ipd" | "medical";
      }>
    ) => {
      if (state.addIPDBill.currState.currSchemeBillHeaderId) {
        const { item, healthCareCategory } = action.payload;
        state.addIPDBill.finalScreen.allSchemeBillItems.cghs.implant[item.id] =
          item;

        if (item.collection) {
          if (
            !state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.cghs.implant.hasOwnProperty(item.collection)
          ) {
            state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.cghs.implant[item.collection] = [item.id];
          } else {
            state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              state.addIPDBill.currState.currSchemeBillHeaderId
            ].billItems.cghs.implant[item.collection].push(item.id);
          }

          const schemeMapping: ISchemeMappingsInHealthCare = {
            itemMappingInSchemeCategory: "cghs",
            itemMappingInSchemeSubCategory: "implant",
            itemMappingInSchemeServiceCategory: item.serviceCategory,
            itemMappingInSchemeId: item.id,
            itemMappingInSchemeCollection: item?.collection,
          };

          item.lid?.forEach((lid) => {
            // Check if lid exists in idp or medical healthcare items

            if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd.hasOwnProperty(
                lid
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
                lid
              ].schemeMappings = [
                ...state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[lid]
                  .schemeMappings,
                schemeMapping,
              ];
            } else if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
                lid
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                lid
              ].schemeMappings = [
                ...state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                  lid
                ].schemeMappings,
                schemeMapping,
              ];
            }
          });
          successMessage("Moved item to CGHS Implant in Scheme Bill", 4);
        }
      } else {
        errorMessage("Cannot add CGHS Implant item");
      }
    },

    updateFilterStrings: (
      state,
      action: PayloadAction<{
        searchString: string;
        type:
          | "cghsIPD"
          | "cghsImplant"
          | "cghsMedical"
          | "cghsPackage"
          | "patientPayableIPD"
          | "patientPayableMedical"
          | "healthCareIPD"
          | "healthCareMedical";
      }>
    ) => {
      const { searchString, type } = action.payload;
      switch (type) {
        case "cghsIPD":
          state.addIPDBill.filters.searchFiltersByComponents.scheme.cghs.ipd =
            searchString;
          return;
        case "cghsImplant":
          state.addIPDBill.filters.searchFiltersByComponents.scheme.cghs.implant =
            searchString;
          return;
        case "cghsMedical":
          state.addIPDBill.filters.searchFiltersByComponents.scheme.cghs.medical =
            searchString;
          return;
        case "cghsPackage":
          state.addIPDBill.filters.searchFiltersByComponents.scheme.cghs.package =
            searchString;
          return;
        case "healthCareIPD":
          state.addIPDBill.filters.searchFiltersByComponents.healthCare.ipd =
            searchString;
          return;
        case "healthCareMedical":
          state.addIPDBill.filters.searchFiltersByComponents.healthCare.medical =
            searchString;
          return;
        case "patientPayableIPD":
          state.addIPDBill.filters.searchFiltersByComponents.scheme.patientPayable.ipd =
            searchString;
          return;
        case "patientPayableMedical":
          state.addIPDBill.filters.searchFiltersByComponents.scheme.patientPayable.medical =
            searchString;
          return;
        default:
          console.error("Type not supported");
      }
    },
    updateIPDListFilters: (
      state,
      action: PayloadAction<{ filters: IIPDListFilters }>
    ) => {
      state.ipdListFilters = action.payload.filters;
    },
    resetPageToIPDBillListMap: (state) => {
      state.pageToIPDBillList = {};
    },
    resetLocalFilesStateIPD: (state) => {
      state.addIPDBill.localFiles = {};
      state.addIPDBill.localFiles2 = [];
    },
    updatePatientAgeAndGender: (state, action: PayloadAction<any>) => {
      try {
        if (!state.addIPDBill.currState.currSchemeBillHeaderId) {
          throw new Error("currHealthCareBillHeaderId is invalid");
        }
        state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          state.addIPDBill.currState.currSchemeBillHeaderId
        ].billHeader = action.payload;
        successMessage("Details updated", 4);
      } catch (error) {
        console.error(error);
        errorMessage("Error in updating details");
      }
    },
    resetSchemeBills: (state) => {
      state.addIPDBill.finalScreen.allSchemeBillItems = {
        cghs: {
          implant: {},
          ipd: {},
          package: {},
          medical: {},
        },
        patientPayable: {
          ipd: {},
          medical: {},
        },
      };
    },
    addNecessaryDetailsForIPD: (
      state,
      action: PayloadAction<IIPDSchemeBillHeader>
    ) => {
      try {
        state.necessaryDetailsForIPD = action.payload;
      } catch (error) {}
    },
    setCollectionCGHSDirect: (
      state,
      action: PayloadAction<{ collection: string; id: number }>
    ) => {
      if (action.payload.id) {
        state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
          action.payload.id
        ].collection = action.payload.collection;
      }
    },
    addExistingBillsDocListToLocalFilesIPD: (
      state,
      action: PayloadAction<Array<IOPDDoc>>
    ) => {
      const billsDocList = action.payload;
      const singleBillDoc = billsDocList?.[0];
      const { documentType } = singleBillDoc;
      state.addIPDBill.localFiles[documentType] = {
        ...singleBillDoc,
      };
      state.addIPDBill.localFiles2 = [...billsDocList];
    },
    updateSchemeBillStatusIPD: (
      state,
      action: PayloadAction<{
        currSchemeBillHeaderId: number | undefined;
        status: string;
      }>
    ) => {
      if (action.payload.currSchemeBillHeaderId) {
        state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          action.payload.currSchemeBillHeaderId
        ].billHeader = {
          ...state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
            action.payload.currSchemeBillHeaderId
          ].billHeader,
          status: action.payload.status,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // getIPDBillList cases
      .addCase(getIPDBillList.pending, (state) => {
        state.isFetchingBillList = true;
      })
      .addCase(
        getIPDBillList.fulfilled,
        (state, action: PayloadAction<IIPDBillListFetchResp>) => {
          state.isFetchingBillList = false;
          state.pageToIPDBillList={};
          state.ipdBillListCurrState.totalElements =
            action.payload.totalElements || 1;
          action.payload.billMappingResponseList?.forEach((bill: IIPDBill) => {
            if (
              state.pageToIPDBillList.hasOwnProperty(
                state.ipdListFilters.pageNumber
              )
            ) {
              state.pageToIPDBillList[state.ipdListFilters.pageNumber].push(
                bill
              );
            } else {
              state.pageToIPDBillList[state.ipdListFilters.pageNumber] = [bill];
            }
          });
        }
      )
      .addCase(getIPDBillList.rejected, (state) => {
        state.isFetchingBillList = false;
      })
      // getIPDQuestionnaire cases
      .addCase(getIPDQuestionnaire.pending, (state) => {
        state.addIPDBill.ipdManualQuestions.isFetchingQuestionnaires = true;
      })
      .addCase(
        getIPDQuestionnaire.fulfilled,
        (
          state,
          action: PayloadAction<{
            ipdQuestionnaires?: Array<IIPDManualQuestion>;
            errorMessage?: string;
            status: number;
          }>
        ) => {
          // TODO: For every fulfilled action, add error scenario for invalid status
          const { ipdQuestionnaires } = action.payload;
          state.addIPDBill.ipdManualQuestions.isFetchingQuestionnaires = false;
          ipdQuestionnaires &&
            ipdQuestionnaires?.forEach(
              (ipQuestionnaire: IIPDManualQuestion) => {
                state.addIPDBill.ipdManualQuestions.ipdQuestionnaires[
                  ipQuestionnaire.id
                ] = ipQuestionnaire;
              }
            );

          const alreadyAnsweredQuestionsLength =
            state.addIPDBill.ipdManualQuestions.answeredQuestionsResponse
              .questionAnswersCollectedResponses.length;
          if (alreadyAnsweredQuestionsLength > 0) {
            state.addIPDBill.ipdManualQuestions.currentQuestionId =
              state.addIPDBill.ipdManualQuestions.answeredQuestionsResponse.questionAnswersCollectedResponses[
                alreadyAnsweredQuestionsLength - 1
              ].questionId;
          }
        }
      )
      .addCase(getIPDQuestionnaire.rejected, (state) => {
        state.addIPDBill.ipdManualQuestions.isFetchingQuestionnaires = false;
      })
      // uploadIPDDoc cases, no need for pending case
      .addCase(
        uploadIPDDoc.fulfilled,
        (
          state,
          action: PayloadAction<{
            docUploadResp?: IIPDDoc;
            status: number;
            errorMessage?: string;
            docType: string;
          }>
        ) => {
          const { docUploadResp, docType } = action.payload;
          state.addIPDBill.localFiles[`${docType}`] = {
            ...state.addIPDBill.localFiles[`${docType}`],
            ...docUploadResp,
            url: docUploadResp?.documentUrl,
          };
          state.addIPDBill.localFiles[`${docType}`].status = "done";
          const mergedObj = {
            ...docUploadResp,
            type: docType,
          };

          if (mergedObj) {
            state.addIPDBill.localFiles2.push(mergedObj);
          }
          state.addIPDBill.localFiles[`${docType}`].status = "done";
          successMessage(`Bill uploaded successfully`);
        }
      )
      .addCase(
        uploadIPDDoc.rejected,
        (
          state,
          action: {
            error: any;
            meta: { arg: { docType: string } };
          }
        ) => {
          const { docType } = action.meta.arg;
          state.addIPDBill.localFiles[`${docType}`].status = "error";
          errorMessage(action.error.message);
        }
      )
      // generateNewIPDBillMapping cases
      .addCase(generateNewIPDBillMapping.pending, (state) => {
        state.addIPDBill.loaders.isFetchIPDBillData = true;
        state.addIPDBill.errors.errorBillMapping = null;
      })
      .addCase(
        generateNewIPDBillMapping.fulfilled,
        (
          state,
          action: PayloadAction<IIPDBillMappingFetchResp | IIPDErrorResp>
        ) => {
          updateIPDBillMappingsUtil(state, action, false);
          state.addIPDBill.loaders.isFetchIPDBillData = false;
        }
      )
      .addCase(generateNewIPDBillMapping.rejected, (state) => {
        state.addIPDBill.loaders.isFetchIPDBillData = false;
        state.addIPDBill.errors.errorBillMapping =
          "Cannot fetch ipd bill mappings";
      })
      // fetchNewIPDHealthCareBillHeader cases
      .addCase(fetchNewIPDHealthCareBillHeader.pending, (state) => {
        state.addIPDBill.loaders.isFetchIPDBillData = true;
        state.addIPDBill.errors.errorHealthCareBillHeader = null;
      })
      .addCase(
        fetchNewIPDHealthCareBillHeader.fulfilled,
        (
          state,
          action: PayloadAction<
            IIPDHealthCareBillHeaderFetchResp | IIPDErrorResp
          >
        ) => {
          fetchNewIPDHealthCareBillHeaderUtil(state, action);
        }
      )
      .addCase(fetchNewIPDHealthCareBillHeader.rejected, (state) => {
        state.addIPDBill.loaders.isFetchIPDBillData = false;
        state.addIPDBill.errors.errorHealthCareBillHeader =
          "Cannot fetch health care bill headers";
      })
      // fetchHealthCarePatientPayable cases
      .addCase(fetchHealthCarePatientPayable.pending, (state) => {
        state.addIPDBill.loaders.isFetchIPDBillData = true;
        state.addIPDBill.errors.errorHealthCarePatientPayable = null;
      })
      .addCase(
        fetchHealthCarePatientPayable.fulfilled,
        (
          state,
          action: PayloadAction<IIPDPatientPayableFetchResp | IIPDErrorResp>
        ) => {
          state.addIPDBill.loaders.isFetchIPDBillData = false;
          // TODO: Check if this can be avoided by passing a isPatientPayable field?
          const healthCareBillHeaderId =
            action.payload.response?.healthCareBillHeaderId;
          if (
            healthCareBillHeaderId &&
            action.payload.response &&
            action.payload.response.data
          ) {
            state.addIPDBill.finalScreen.healthCareBillIdDataMap[
              healthCareBillHeaderId
            ].patientPayableData = action.payload.response?.data;
          }
        }
      )
      .addCase(fetchHealthCarePatientPayable.rejected, (state) => {
        state.addIPDBill.loaders.isFetchIPDBillData = false;
        state.addIPDBill.errors.errorHealthCarePatientPayable =
          "Cannot fetch health care patient payable";
      })
      // fetchIPDQuestionAnswerCollection cases
      .addCase(fetchIPDQuestionAnswerCollection.pending, (state) => {
        state.addIPDBill.loaders.isFetchIPDBillData = true;
        state.addIPDBill.errors.errorQuestionAnswerCollection = null;
      })
      .addCase(
        fetchIPDQuestionAnswerCollection.fulfilled,
        (
          state,
          action: PayloadAction<IIPDFetchQuestionAnswersResp | IIPDErrorResp>
        ) => {
          state.addIPDBill.loaders.isFetchIPDBillData = false;
          if (action.payload.response) {
            state.addIPDBill.ipdManualQuestions.answeredQuestionsResponse =
              action.payload.response;
            if (action.payload.response.conclusion !== "") {
              state.addIPDBill.ipdManualQuestions.isConclusionGenerated = true;
            }
          }
        }
      )
      .addCase(fetchIPDQuestionAnswerCollection.rejected, (state) => {
        state.addIPDBill.loaders.isFetchIPDBillData = false;
        state.addIPDBill.errors.errorQuestionAnswerCollection =
          "Cannot fetch ipd question answer collection";
      })
      // fetchIPDBillSchemeHeader cases
      .addCase(fetchIPDBillSchemeHeader.pending, (state) => {
        state.addIPDBill.loaders.isFetchIPDBillData = true;
        state.addIPDBill.errors.errorBillSchemeHeader = null;
      })
      .addCase(
        fetchIPDBillSchemeHeader.fulfilled,
        (
          state,
          action: PayloadAction<IIPDSchemeHeaderFetchResp | IIPDErrorResp>
        ) => {
          try {
            updateSchemeDataUtil(state, action);
          } catch (error) {
            state.addIPDBill.errors.errorBillSchemeHeader =
              "Cannot fetch ipd bill scheme headers";
          } finally {
            state.addIPDBill.loaders.isFetchIPDBillData = false;
          }
        }
      )
      .addCase(fetchIPDBillSchemeHeader.rejected, (state) => {
        state.addIPDBill.loaders.isFetchIPDBillData = false;
        state.addIPDBill.errors.errorBillSchemeHeader =
          "Cannot fetch ipd bill scheme headers";
      })

      .addCase(resetSchemeBillItem.pending, (state) => {
        state.addIPDBill.loaders.isResetBillItems = true;
        state.addIPDBill.errors.errorBillSchemeHeader = null;
      })
      .addCase(
        resetSchemeBillItem.fulfilled,
        (
          state,
          action: PayloadAction<IIPDSchemeHeaderFetchResp | IIPDErrorResp>
        ) => {
          try {
            updateSchemeDataUtil(state, action);
          } catch (error) {
            state.addIPDBill.errors.errorBillSchemeHeader =
              "Cannot fetch ipd bill scheme headers";
          } finally {
            state.addIPDBill.loaders.isResetBillItems = false;
          }
        }
      )
      .addCase(resetSchemeBillItem.rejected, (state) => {
        state.addIPDBill.loaders.isResetBillItems = false;
        state.addIPDBill.errors.errorBillSchemeHeader =
          "Cannot fetch ipd bill scheme headers";
      })

      //post answered questions manually response
      .addCase(submitQuestionAnswersCollection.pending, (state) => {
        state.addIPDBill.errors.errorQuestionAnswerCollection = null;
      })
      .addCase(
        submitQuestionAnswersCollection.fulfilled,
        (
          state,
          action: PayloadAction<IIPDFetchQuestionAnswersResp | IIPDErrorResp>
        ) => {
          if (action.payload.response) {
            state.addIPDBill.ipdManualQuestions.answeredQuestionsResponse =
              action.payload.response;
            if (action.payload.response.conclusion !== "") {
              state.addIPDBill.ipdManualQuestions.isConclusionGenerated = true;
            }
          }
        }
      )
      .addCase(submitQuestionAnswersCollection.rejected, (state) => {
        state.addIPDBill.errors.errorQuestionAnswerCollection =
          "Cannot post ipd question answer collection";
      }) // fetchEditIPDBillMapping cases
      .addCase(fetchEditIPDBillMapping.pending, (state) => {
        state.addIPDBill.loaders.isFetchIPDBillData = true;
        state.addIPDBill.errors.errorBillMapping = null;
      })
      .addCase(
        fetchEditIPDBillMapping.fulfilled,
        (
          state,
          action: PayloadAction<IIPDBillMappingFetchResp | IIPDErrorResp>
        ) => {
          updateIPDBillMappingsUtil(state, action, true);
          state.addIPDBill.loaders.isFetchIPDBillData = false;
        }
      )
      .addCase(fetchEditIPDBillMapping.rejected, (state) => {
        state.addIPDBill.loaders.isFetchIPDBillData = false;
        state.addIPDBill.errors.errorBillMapping =
          "Cannot fetch ipd bill mappings";
      })
      // updateSchemeBills cases
      .addCase(updateSchemeBills.pending, (state) => {
        state.addIPDBill.errors.errorInUpdatingSchemeBillItems = null;
        state.addIPDBill.loaders.isSchemeBillItemsUpdating = true;
      })
      .addCase(
        updateSchemeBills.fulfilled,
        (
          state,
          action: PayloadAction<IIPDSchemeHeaderFetchResp | IIPDErrorResp>
        ) => {
          state.addIPDBill.loaders.isSchemeBillItemsUpdating = false;
          if (action.payload.response) {
            updateSchemeDataUtil(state, action);
            successMessage("Scheme bill items updated successfully", 4);
          } else {
            errorMessage("Cannot update Scheme bill items");
          }
        }
      )
      .addCase(updateSchemeBills.rejected, (state) => {
        state.addIPDBill.errors.errorInUpdatingSchemeBillItems =
          "Cannot update scheme bill items";
        state.addIPDBill.loaders.isSchemeBillItemsUpdating = false;
        errorMessage("Error in updating scheme bill items", 4);
      }) // approveIPDBill cases
      .addCase(approveIPDBill.pending, (state) => {
        state.addIPDBill.errors.errorInApprovingIPDBill = null;
        state.addIPDBill.loaders.isApprovingIPDBill = true;
      })
      .addCase(
        approveIPDBill.fulfilled,
        (
          state,
          action: PayloadAction<
            { response: string; status: number } | IIPDErrorResp
          >
        ) => {
          state.addIPDBill.loaders.isApprovingIPDBill = false;
          successMessage("BILL STATUS UPDATED SUCCESSFULLY");
        }
      )
      .addCase(approveIPDBill.rejected, (state) => {
        // state.addIPDBill.errors.errorInApprovingIPDBill =
        //   "Cannot update scheme bill items";
        state.addIPDBill.loaders.isApprovingIPDBill = false;
        errorMessage("ERROR IN UPDATING BILL STATUS");
      })
      .addCase(uploadRelatedDoc.pending, (state) => {
        state.addIPDBill.loaders.isSchemeBillItemsUpdating = true;
      })
      .addCase(
        uploadRelatedDoc.fulfilled,
        (
          state,
          action: PayloadAction<{
            docUploadResp?: IIPDDoc;
            status: number;
            errorMessage?: string;
            docType: string;
          }>
        ) => {
          state.addIPDBill.loaders.isSchemeBillItemsUpdating = true;

          const currentHeaderId =
            state.addIPDBill.currState.currSchemeBillHeaderId;
          if (currentHeaderId) {
            const { docType, docUploadResp } = action.payload;
            const currentBillHeader = {
              ...state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
                currentHeaderId
              ].billHeader,
            };

            if (currentBillHeader) {
              switch (docType) {
                case "DISCHARGE SUMMARY":
                  currentBillHeader.s3DocDischargeSummary =
                    docUploadResp?.documentUrl;
                  break;
                case "REFERRAL LETTER":
                  currentBillHeader.s3DocReferralLetter =
                    docUploadResp?.documentUrl;
                  break;
                case "OTHERS":
                  currentBillHeader.s3DocOthers = docUploadResp?.documentUrl;
                  break;
                default:
                  break;
              }
            }

            state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
              currentHeaderId
            ].billHeader = currentBillHeader;
          }

          state.addIPDBill.loaders.isSchemeBillItemsUpdating = false;
        }
      )
      .addCase(uploadRelatedDoc.rejected, (state) => {
        state.addIPDBill.loaders.isSchemeBillItemsUpdating = true;
      });
  },
});

export const {
  clearNecessaryDetailsForIPD,
  updateClaimId,
  updateSchemePackage,
  updateSchemeBillItem,
  updateAddIPDBillWizardCurrScreenNumber,
  updateAddIPDBillWizardCurrScreenValues,
  resetAddIPDBillState,
  addIPDBillUploadedFile,
  removeIPDBillUploadedFile,
  removeIPDBillUploadedFile2,
  addAnsweredQuestion,
  editAnsweredQuestion,
  removeSchemeBillItem,
  resetAnsweredQuestions,
  toggleHighlightPackagedItems,
  toggleHighlightPatientPayableItems,
  updateIPDHealthCareBillItem,
  updateMedicalHealthCareBillItem,
  saveRelatedDocLocally,
  updateCardCategory,
  updateObservations,
  updateLidsInPackage,
  addCGHSIPDItemInScheme,
  resetLocalFilesStateIPD,
  addPatientPayableIPDItemInScheme,
  addCGHSImplantItemInScheme,
  addCGHSMedicalItemInScheme,
  addPatientPayableMedicalItemInScheme,
  updateFilterStrings,
  updateIPDListFilters,
  resetPageToIPDBillListMap,
  updatePatientAgeAndGender,
  resetSchemeBills,
  resetCurrBillState,
  resetIPDManualQues,
  setAnsweredQuestionResponse,
  addNecessaryDetailsForIPD,
  addCGHSMedicalToIPDItemInScheme,
  setCollectionCGHSDirect,
  addExistingBillsDocListToLocalFilesIPD,
  updateSchemeBillStatusIPD,
} = ipdBillsSlice.actions;
export default ipdBillsSlice.reducer;
